import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { beautifyNumber } from '../../../Utils/FormatNumber';

const childAmount = (child) => {
    if (child.amount1 != null) return child.amount1;
    const debit = Number(child.debitAccount) || 0;
    const credit = Number(child.creditAccount) || 0;
    return debit !== 0 ? debit : credit;
};

const childLabel = (child) =>
    [child.command, child.reference1 ?? child.reference, child.description].filter(Boolean).join(' · ');

/**
 * Label for a row collapsed by the reconciliation Summary view: the row text plus a chip with the
 * number of collapsed rows; hovering the chip lists them.
 */
const SummaryRowLabel = ({ row, text, t, onToggle, expanded = false }) => {
    const children = row?._children || [];
    const tooltip = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {children.map((child, index) => (
                <span key={`${child.id}-${index}`}>
                    {childLabel(child) || '-'}: {beautifyNumber(childAmount(child))}
                </span>
            ))}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, width: '100%' }}>
            <Tooltip title={tooltip} arrow placement="top">
                <Chip
                    size="small"
                    label={t('GroupedItemsCount', { count: row?._count ?? children.length })}
                    // The caret says the chip opens the rows behind the merge, not just counts them.
                    icon={onToggle
                        ? (expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />)
                        : undefined}
                    onClick={onToggle ? (e) => { e.stopPropagation(); onToggle(); } : undefined}
                    color={expanded ? 'primary' : 'default'}
                    variant={expanded ? 'filled' : 'outlined'}
                    sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                        borderColor: '#304FFF',
                        color: expanded ? '#fff' : '#304FFF',
                        '& .MuiChip-icon': { color: 'inherit', marginInlineStart: '4px', marginInlineEnd: '-4px' },
                    }}
                />
            </Tooltip>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Box>
    );
};

/** One of the rows behind an expanded summary row: arrow plus indent, so it reads as a sub-item. */
export const SummaryChildLabel = ({ text }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, width: '100%' }}>
        <SubdirectoryArrowLeftIcon
            sx={{ fontSize: 14, color: '#304FFF', opacity: 0.65, flexShrink: 0, marginInlineStart: '14px' }}
        />
        <Box
            component="span"
            sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'var(--bank-text-muted, #6b7186)',
            }}
        >
            {text}
        </Box>
    </Box>
);

/** Row chrome for a sub-item: quieter surface and a dashed rail back to its summary row. */
export const summaryChildRowSx = {
    backgroundColor: 'rgba(48, 79, 255, 0.035)',
    borderInlineStart: '3px dashed rgba(48, 79, 255, 0.4)',
};

export default SummaryRowLabel;
